import React, { useEffect, useState } from 'react';
import { CheckCircle2, Loader2, Upload, X } from 'lucide-react';
import { apiCall } from '../_shared';

export const priceForLabel = (pf) => {
  if (!pf) return '';
  if (pf.type === 'upToPerson') return `up to ${pf.upToPersonValue} people`;
  if (pf.type === 'maxCapacity') return 'max capacity';
  return pf.type || '';
};

const RunRow = ({ r }) => {
  const keys = Object.keys(r.vals);
  const cells = keys.map((k) => <span key={k} className="mr-2"><span className="text-slate-400">{k.replace('price', 'P')}{k === 'minStay' ? '' : ''}:</span> {r.before?.[k] != null ? <span className="line-through text-slate-400 mr-1">{r.before[k]}</span> : null}<b>{r.vals[k]}</b></span>);
  return <li className="flex justify-between gap-2 text-xs border-t py-1" data-testid={`rate-run-${r.from}`}><span className="text-slate-700">{r.from}{r.to !== r.from ? ` → ${r.to}` : ''} <span className="text-slate-400">({r.nights} n)</span></span><span>{cells}</span></li>;
};

// Two-step: dry run (preview old → new per date run) then the real push. `request` = {path, body}.
export const RatePushModal = ({ title, request, onClose, onDone }) => {
  const [preview, setPreview] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  useEffect(() => {
    let alive = true;
    apiCall(request.path, { method: 'POST', body: JSON.stringify({ ...request.body, dry_run: true }) })
      .then((d) => { if (alive) setPreview(d); }).catch((e) => { if (alive) setErr(e.message); });
    return () => { alive = false; };
  }, [request]);
  const confirm = async () => {
    setBusy(true); setErr('');
    try {
      const d = await apiCall(request.path, { method: 'POST', body: JSON.stringify({ ...request.body, dry_run: false }) });
      setDone(d); onDone?.(d);
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };
  const runs = (preview?.runs || []).map((r) => <RunRow key={r.from} r={r} />);
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="rate-push-modal">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg p-5 max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between gap-2">
          <h3 className="text-sm font-bold text-slate-900">{title}</h3>
          <button type="button" onClick={onClose} className="p-1 rounded hover:bg-slate-100" data-testid="rate-push-close"><X className="w-4 h-4" /></button>
        </div>
        {preview?.target && <p className="text-xs text-slate-600 mt-1">Target: <b>{preview.target}</b> ({preview.from} → {preview.to}) · {preview.nights} nights affected</p>}
        {err && <p className="text-xs text-red-700 mt-2" data-testid="rate-push-error">{err}</p>}
        {!preview && !err && <p className="text-xs text-slate-500 mt-3 flex items-center gap-1"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Reading current Beds24 prices…</p>}
        {preview && !done && (
          <>
            <p className="text-[11px] text-slate-500 mt-3">Old → new, grouped by identical runs. Rates propagate from Beds24 to Booking.com and every connected channel.</p>
            <ul className="mt-1 max-h-64 overflow-y-auto" data-testid="rate-push-runs">{runs}</ul>
            <div className="flex gap-2 mt-4">
              <button type="button" onClick={confirm} disabled={busy} className="inline-flex items-center gap-1 text-sm bg-emerald-600 text-white rounded-lg px-4 py-2 disabled:opacity-50" data-testid="rate-push-confirm">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Push to Beds24</button>
              <button type="button" onClick={onClose} className="text-sm border rounded-lg px-4 py-2" data-testid="rate-push-cancel">Cancel</button>
            </div>
          </>
        )}
        {done && (
          <div className="mt-4 rounded-lg bg-emerald-50 border border-emerald-200 p-3 text-sm text-emerald-900" data-testid="rate-push-done">
            <p className="font-semibold flex items-center gap-1"><CheckCircle2 className="w-4 h-4" /> Rates updated in Beds24 — {done.nights} nights.</p>
            <button type="button" onClick={onClose} className="mt-2 text-xs underline" data-testid="rate-push-finish">Close</button>
          </div>
        )}
      </div>
    </div>
  );
};
